import React, { useMemo, useState } from 'react';
import { CalendarDays, Clock, Newspaper } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import ContactForm from '../ContactForm';
import PageHeader from '../components/PageHeader';

const EditorialDigitalPage = () => {
  const { t, i18n } = useTranslation();
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  const openContactPopup = () => {
    setIsPopupOpen(true);
  };

  const closePopup = () => {
    setIsPopupOpen(false);
  };

  const dateString = useMemo(() => {
    const today = new Date();
    const locale = i18n.language === 'pt' ? 'pt-PT' : i18n.language === 'en' ? 'en-US' : 'es-ES';
    return today.toLocaleDateString(locale, {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  }, [i18n.language]);

  const timeString = useMemo(() => {
    const today = new Date();
    const locale = i18n.language === 'pt' ? 'pt-PT' : i18n.language === 'en' ? 'en-US' : 'es-ES';
    return today.toLocaleTimeString(locale, { hour: '2-digit', minute: '2-digit' });
  }, [i18n.language]);

  const columns = useMemo(() => t('editorial.columns', { returnObjects: true }), [t]);
  const lang = (i18n.resolvedLanguage || i18n.language || 'es').slice(0, 2);
  const c = {
    es: {
      mainAlt: 'Informe Principal',
      editions: [
        { name: 'Operaciones', no: 'Nº 124' },
        { name: 'Atencion al Cliente', no: 'Nº 87' },
        { name: 'Datos y Analitica', no: 'Nº 56' },
        { name: 'Automatizacion', no: 'Nº 41' },
      ],
      topics: ['Procesos', 'ROI', 'Calidad', 'Experiencia', 'Omnicanalidad'],
    },
    en: {
      mainAlt: 'Main Report',
      editions: [
        { name: 'Operations', no: 'No. 124' },
        { name: 'Customer Service', no: 'No. 87' },
        { name: 'Data and Analytics', no: 'No. 56' },
        { name: 'Automation', no: 'No. 41' },
      ],
      topics: ['Processes', 'ROI', 'Quality', 'Experience', 'Omnichannel'],
    },
    pt: {
      mainAlt: 'Relatorio Principal',
      editions: [
        { name: 'Operacoes', no: 'Nº 124' },
        { name: 'Atendimento ao Cliente', no: 'Nº 87' },
        { name: 'Dados e Analitica', no: 'Nº 56' },
        { name: 'Automacao', no: 'Nº 41' },
      ],
      topics: ['Processos', 'ROI', 'Qualidade', 'Experiencia', 'Omnicanalidade'],
    },
  }[lang] || {};

  return (
    <div className="min-h-screen bg-[#f7f7f5] font-inter">
      <PageHeader onOpenPopup={openContactPopup} />
      
      <main className="pt-20">
        <section className="border-y border-gray-300 bg-white">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <div className="flex items-center justify-between py-3 text-gray-700">
              <div className="flex items-center gap-2">
                <Newspaper className="w-5 h-5" />
                <span className="uppercase tracking-widest text-xs font-bold">{t('editorial.badge')}</span>
              </div>
              <div className="flex items-center gap-4 text-sm font-medium">
                <span className="flex items-center gap-1"><CalendarDays className="w-4 h-4 text-gray-400" />{dateString}</span>
                <span className="flex items-center gap-1"><Clock className="w-4 h-4 text-gray-400" />{timeString}</span>
              </div>
            </div>
          </div>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 py-12 md:py-20">
          <h1 className="font-serif text-4xl md:text-6xl font-bold tracking-tight text-[#141414] text-center mb-8 leading-tight max-w-5xl mx-auto">
            {t('editorial.title')}
          </h1>
          <p className="text-center text-gray-600 text-xl md:text-2xl max-w-3xl mx-auto font-serif italic">
            {t('editorial.subtitle')}
          </p>
        </section>

        <section className="max-w-7xl mx-auto px-4 sm:px-6 pb-24">
          <div className="grid lg:grid-cols-3 gap-12">
            <div className="lg:col-span-1 lg:border-r border-gray-300 lg:pr-12">
              <article className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm hover:shadow-md transition-shadow">
                <img
                  src="https://placehold.co/1200x700/f0f0f0/171717?text=Informe+Principal"
                  alt={c.mainAlt}
                  className="w-full h-64 object-cover"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = "https://placehold.co/1200x700/f0f0f0/171717?text=Main+Report";
                  }}
                />
                <div className="p-8">
                  <h2 className="font-serif text-3xl font-bold text-[#141414] leading-tight mb-4">
                    {t('editorial.main_article.title')}
                  </h2>
                  <p className="text-gray-700 leading-relaxed mb-8">
                    {t('editorial.main_article.desc')}
                  </p>
                  <button
                    onClick={openContactPopup}
                    className="bg-[#141414] text-white px-8 py-3 rounded-full text-sm font-bold hover:bg-gray-800 transition shadow-lg"
                  >
                    {t('editorial.cta_session')}
                  </button>
                </div>
              </article>
            </div>

            <div className="lg:col-span-1 lg:border-r border-gray-300 lg:pr-12">
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                <div className="p-6 border-b border-gray-100 bg-gray-50/50">
                  <h3 className="font-serif text-2xl font-bold text-[#141414]">{t('editorial.columns_title')}</h3>
                </div>
                <div className="divide-y divide-gray-100">
                  {columns.map((col) => (
                    <div key={col.title} className="p-6 hover:bg-gray-50 transition-colors">
                      <h4 className="font-serif text-xl font-bold text-[#141414] mb-2">{col.title}</h4>
                      <p className="text-gray-600 text-sm leading-relaxed">
                        {col.desc}
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            <aside className="space-y-8">
              <div className="bg-white border border-gray-200 rounded-lg overflow-hidden shadow-sm">
                <div className="p-6 border-b border-gray-100 bg-gray-50/50">
                  <h3 className="font-serif text-2xl font-bold text-[#141414]">{t('editorial.editions_title')}</h3>
                </div>
                <div className="p-6">
                  <ul className="space-y-4 text-sm text-gray-700 font-medium">
                    {(c.editions || []).map((edition) => (
                      <li key={edition.name} className="flex justify-between items-center group cursor-pointer">
                        <span className="group-hover:text-blue-600 transition-colors">{edition.name}</span>
                        <span className="text-gray-400 bg-gray-100 px-2 py-0.5 rounded text-[10px]">{edition.no}</span>
                      </li>
                    ))}
                  </ul>
                </div>
                <div className="border-t border-gray-100 p-6">
                  <h3 className="font-serif text-xl font-bold text-[#141414] mb-4">{t('editorial.topics_title')}</h3>
                  <div className="flex flex-wrap gap-2">
                    {(c.topics || []).map(topic => (
                      <span key={topic} className="px-3 py-1 text-xs font-bold bg-gray-100 text-gray-600 rounded-full hover:bg-gray-200 cursor-pointer transition-colors">
                        {topic}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="bg-[#141414] text-white rounded-lg p-8 shadow-xl relative overflow-hidden">
                <div className="absolute top-0 right-0 w-32 h-32 bg-white/5 rounded-full blur-2xl -mr-16 -mt-16"></div>
                <h3 className="font-serif text-2xl font-bold mb-4 relative z-10">{t('editorial.subscribe_title')}</h3>
                <p className="text-white/70 text-sm leading-relaxed mb-8 relative z-10">
                  {t('editorial.subscribe_desc')}
                </p>
                <button
                  onClick={openContactPopup}
                  className="w-full bg-white text-[#141414] py-3 rounded-full text-sm font-bold hover:bg-gray-100 transition relative z-10"
                >
                  {t('editorial.subscribe_btn')}
                </button>
              </div>
            </aside>
          </div>
        </section>
      </main>

      {isPopupOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm px-4" onClick={(e) => { if (e.target === e.currentTarget) closePopup(); }}>
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full overflow-hidden relative">
            <button onClick={closePopup} className="absolute top-4 right-4 text-gray-400 hover:text-gray-800 transition z-10">
              <X size={24} />
            </button>
            <div className="px-8 py-6 border-b border-gray-100 bg-gray-50/50">
              <h4 className="font-serif text-2xl font-bold text-[#141414] mb-1">{t('editorial.popup.title')}</h4>
              <p className="text-gray-500 text-sm">{t('editorial.popup.desc')}</p>
            </div>
            <div className="p-8">
              <ContactForm
                scriptSrc="https://cdn.bitrix24.es/b13766179/crm/form/loader_13.js"
                formData="b13766179"
                formId="13"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const X = ({ size, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <path d="M18 6 6 18" />
    <path d="m6 6 12 12" />
  </svg>
);

export default EditorialDigitalPage;

